import { Injectable } from '@angular/core';
import { PreloadingStrategy, Route } from '@angular/router';
import { EMPTY, Observable } from 'rxjs';

/**
 * Stratégie de préchargement opt-in.
 *
 * Seules les routes marquées `data: { preload: true }` sont préchargées,
 * pendant les périodes d'inactivité du navigateur (requestIdleCallback),
 * et jamais en mode économie de données ou sur réseau 2g.
 *
 * Remplace PreloadAllModules qui téléchargeait TOUS les chunks lazy
 * (y compris @formio/js ~1,7 MB) en arrière-plan dès l'arrivée sur
 * n'importe quelle page, en concurrence avec les ressources de la page
 * affichée.
 *
 * Le flag se pose par niveau : les enfants d'une route lazy ne sont
 * considérés qu'une fois sa config chargée (ex. les pages admin marquées
 * `preload` ne se préchargent qu'après une première navigation vers
 * /panel — jamais pour un visiteur anonyme).
 */
@Injectable({ providedIn: 'root' })
export class SelectivePreloadStrategy implements PreloadingStrategy {
  preload(route: Route, load: () => Observable<unknown>): Observable<unknown> {
    if (!route.data?.['preload'] || this.isConstrainedConnection()) {
      return EMPTY;
    }
    return new Observable(subscriber => {
      scheduleWhenIdle(() => load().subscribe(subscriber));
    });
  }

  /** SSR, Save-Data actif ou réseau 2g : on ne précharge rien. */
  private isConstrainedConnection(): boolean {
    if (typeof navigator === 'undefined') return true;
    const connection = (navigator as { connection?: { saveData?: boolean; effectiveType?: string } }).connection;
    if (!connection) return false;
    return connection.saveData === true || /2g/.test(connection.effectiveType ?? '');
  }
}

function scheduleWhenIdle(callback: () => void): void {
  if (typeof requestIdleCallback === 'function') {
    requestIdleCallback(() => callback(), { timeout: 4000 });
  } else {
    setTimeout(callback, 2000);
  }
}
